import React, { useEffect, useState } from 'react';
import { PageHeader } from '../components/page/PageHeader';
import { FeatureRow } from '../components/page/FeatureRow';
import { CtaBand } from '../components/page/CtaBand';
import { AgencyModal } from '../components/AgencyModal';
import { PlayCircle } from 'lucide-react';
import { LayeredRosterMockup, LayeredSubmissionsMockup, LayeredMediaKitMockup, LayeredProfileOwnershipMockup, LayeredDiscoverabilityMockup } from '../components/page/TalentMockups';
import { RobinIntegration } from '../components/page/RobinIntegration';
import { FeaturedTalentTestimonial } from '../components/FeaturedTalentTestimonial';
import { Testimonials, TALENT_TESTIMONIALS } from '../components/Testimonials';
const DEMO_VIDEO_URL = 'https://www.youtube.com/watch?v=d0T7ZbxsxrY';
const SEO = {
  title: 'List Your Talent Agency Roster | Zano',
  description: 'List your talent agency and creator roster on Zano to showcase your represented talent to brands and marketing agencies.',
  keywords: 'talent agency software, influencer agency software, talent agency roster, represented creators, media kit, influencer casting calls, creator roster, talent management',
  ogTitle: 'List Your Talent Agency Roster | Zano',
  ogDescription: 'List your talent agency and creator roster on Zano to showcase your represented talent to brands and marketing agencies.',
  ogUrl: 'https://zanoplatform.com/list-your-roster'
};
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
export const ForTalent = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreKeywords = updateMetaTag('name', 'keywords', SEO.keywords);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    const restoreOgUrl = updateMetaTag('property', 'og:url', SEO.ogUrl);
    return () => {
      document.title = previousTitle;
      restoreOgUrl();
      restoreDescription();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  return <>
      <AgencyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-2622" data-mp="1fch9sjezhdm8" />
      <PageHeader eyebrow="For Talent Agencies" eyebrowClass="bg-orange-50 text-orange-700 border border-orange-200" title="Get more deals for your creators" subtitle="We help talent agencies win more brand partnerships by showcasing their live creator roster in our marketplace, enabling submissions to casting calls, and providing free public media kits to promote their talent." primaryCta={{
      label: 'List Your Agency',
      onClick: () => setIsModalOpen(true)
    }} secondaryCta={{
      label: 'Watch demo video',
      href: DEMO_VIDEO_URL,
      newTab: true,
      icon: <PlayCircle size={18} className="mr-1.5" data-id="element-2624" data-mp="a8bzzh7j6wcg" />
    }} data-id="element-2623" data-mp="17ujg9od76d4n" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 divide-y divide-gray-100" data-id="element-2625" data-mp="65z6ps9z0qwk">
        <FeatureRow eyebrow="Roster Management" title="Manage your roster in a familiar table view" body="See your entire roster at a glance. We built it purposely in a table format because we know you're used to Google Sheets. Sort, search, and filter by followers, average views, engagement rate, niche, location, and keywords." bullets={['Familiar, easy-to-use table interface', 'Advanced filtering for quick talent matching', 'Easy bulk upload with Google Sheets']} visual={<LayeredRosterMockup data-id="element-2627" data-mp="1hjl5ytw4xv3q" />} data-id="element-2626" data-mp="67s56kafk4th" />
        <FeatureRow eyebrow="Inbound Deals" title="Submit to casting calls in seconds" body="Submit your creators to brand casting calls with the click of a button. You can search and filter your roster directly in the submission window—like finding everyone with 50K+ followers and 100K+ average views—then add your custom rate and notes." bullets={['Filter your roster directly within submissions', 'Add custom rates and notes for the brand', 'Win more deals with tailored pitches']} visual={<LayeredSubmissionsMockup data-id="element-2629" data-mp="3hz4sgvpwvjdx" />} flip data-id="element-2628" data-mp="1mk7tmbl8uogn" />
        <FeatureRow eyebrow="Outbound Pitching" title="Public roster media kits" body="Share a live, public version of your roster table with brands. Our media kits include built-in filters and search, making it incredibly easy for brands to browse. Many agencies use this to successfully upsell brands on additional talent." bullets={['Live audience data that updates automatically', 'Built-in search and filters for brands', 'Proven to help upsell additional creators']} visual={<LayeredMediaKitMockup data-id="element-2631" data-mp="9l60e5628i5p" />} data-id="element-2630" data-mp="3d3pem3rwlnhi" />
        <FeatureRow eyebrow="Profile Ownership" title="Keep your team attached to every profile" body="Every creator profile puts your agency, representation status, and named talent manager front and center—so brands and marketing agencies contact the right person directly." bullets={['Your agency name and exclusivity stay visible', 'Named talent manager with direct contact details', 'Profile details, tags, and live performance stats in one place']} visual={<LayeredProfileOwnershipMockup data-id="element-2633" data-mp="ta8bd26fgqbm" />} data-id="element-2632" data-mp="9mgzvssh33r0" />
        <FeatureRow eyebrow="Discoverability" title="Get discovered by top brands" body="By listing your agency on Zano, your roster becomes searchable in our global database. Brands and marketing agencies can find your talent and reach out directly to your inbox with new opportunities." bullets={['Appear in the global roster database', 'Receive direct inbound inquiries from brands', "Grow your agency's industry presence"]} visual={<LayeredDiscoverabilityMockup data-id="element-2635" data-mp="1l0uy8f7g0nt7" />} flip data-id="element-2634" data-mp="t7m28y3w22ra" />
      </div>

      <RobinIntegration data-id="element-2636" data-mp="1kys4bk366aj7" />

      <FeaturedTalentTestimonial data-id="element-2637" data-mp="35inn3gpu69pm" />

      <Testimonials title="What other talent agencies are saying" items={TALENT_TESTIMONIALS} data-id="element-2638" data-mp="35joxrk2xwu6t" />

      <CtaBand title="Get your roster in front of the right brands" subtitle="List your agency on Zano and start winning more deals for your creators." primaryCta={{
      label: 'List Your Agency',
      onClick: () => setIsModalOpen(true)
    }} secondaryCta={{
      label: 'Watch demo video',
      href: DEMO_VIDEO_URL,
      newTab: true,
      icon: <PlayCircle size={18} className="mr-1.5" data-id="element-2640" data-mp="3k9wgm0wv0f81" />
    }} data-id="element-2639" data-mp="31g0n2sj0hihc" />
    </>;
};